import React, {ReactNode, useEffect, useRef} from 'react';
import {useInView} from 'react-intersection-observer';
import {usePositionMarker} from './position-marker-context';

/**
 * Props for the PositionMarkerContainer component
 */
interface PositionMarkerContainerProps {
    /** Child component to track */
    children: ReactNode;
    /** Threshold percentage (0-1) for when component is considered out of view. Default: 0.1 (10%) */
    threshold?: number;
    /** Additional CSS class name */
    className?: string;
}

/**
 * Container component that tracks when its child component enters/exits the viewport
 * Uses intersection observer to detect visibility changes and updates the position marker context
 */
export function PositionMarkerContainer({
    children,
    threshold = 0.1,
    className,
}: PositionMarkerContainerProps) {
    const {setComponentOutOfView} = usePositionMarker();
    const isPageVisible = useRef(true); // Default to true for SSR

    // Track page visibility to avoid triggering on tab switches
    useEffect(() => {
        const handleVisibilityChange = () => {
            isPageVisible.current = !document.hidden;
        };

        // Initialize with current state (only on client)
        if (typeof document !== 'undefined') {
            isPageVisible.current = !document.hidden;
            document.addEventListener('visibilitychange', handleVisibilityChange);
        }

        return () => {
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            }
        };
    }, []);

    const {ref} = useInView({
        threshold,
        triggerOnce: false,
        onChange: (inView) => {
            // Only update context if the page is visible (user hasn't switched tabs)
            if (isPageVisible.current) {
                setComponentOutOfView(inView);
            }
        },
    });

    return (
        <div ref={ref} className={className}>
            {children}
        </div>
    );
}
